import React, { useEffect, useMemo, useState } from "react";
import { Block } from "@randajan/jet-react/dom/block";
import { Alert, Box, Typography } from "@mui/material";

import { validateProfileForm } from "../../../../../arc/forms/profileForms.js";
import { Say } from "../../../../../arc/locale/consts.js";
import { meBeam } from "../../../../assets/beams/meBeam";
import { BlimpMuiFormActions } from "../../../../elements/BlimpMuiFormActions/BlimpMuiFormActions";
import { BlimpMuiFormSection } from "../../../../elements/BlimpMuiFormSection/BlimpMuiFormSection";
import { Locale } from "../../../../assets/Locale.js";
import { useBeforeUnload } from "../../../../assets/hooks/useBeforeUnload.jsx";
import { getValidationFieldError, handleProfileSubmitReply, mergeValidation } from "../profileValidation.js";
import { personalFormFields, personalFormSections } from "./formConfig";
import { PersonalFormField } from "./PersonalFormField/PersonalFormField";

import "./PersonalForm.scss";

const emptyValues = Object.freeze({});
const personalFieldNames = Object.freeze(personalFormFields.map(field=>field.name));
const fieldsByName = Object.freeze(Object.fromEntries(personalFormFields.map(field=>[field.name, field])));

const toDateInput = value=>{
    if (!value) { return ""; }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) { return ""; }
    return date.toISOString().slice(0, 10);
};

const toInputValue = (profile, field)=>{
    const value = profile?.[field.name];
    if (field.type === "date") { return toDateInput(value); }
    return value == null ? "" : String(value);
};

const createValues = (profile={})=>{
    return Object.fromEntries(personalFormFields.map(field=>[
        field.name,
        toInputValue(profile, field)
    ]));
};

const touchAllFields = _=>Object.fromEntries(personalFieldNames.map(field=>[field, true]));

export const PersonalForm = (props)=>{
    const { profile, onSaved, onCancel } = props;
    const say = Locale.use();
    const initialValues = useMemo(()=>createValues(profile), [profile]);
    const [ values, setValues ] = useState(initialValues);
    const [ touched, setTouched ] = useState(emptyValues);
    const [ submitState, setSubmitState ] = useState("");
    const [ serverValidation, setServerValidation ] = useState(null);
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    useEffect(()=>{
        setValues(initialValues);
        setTouched(emptyValues);
        setServerValidation(null);
        setSubmitState("");
    }, [initialValues]);

    useBeforeUnload();

    const localValidation = useMemo(()=>validateProfileForm("personal", values), [values]);
    const validation = useMemo(()=>mergeValidation(localValidation, serverValidation), [localValidation, serverValidation]);
    const showAllErrors = submitState === "validationError";

    const setFieldValue = event=>{
        const { name, value } = event.target;
        setValues(current=>({ ...current, [name]:value }));
        setServerValidation(null);
        setSubmitState("");
    };

    const touchField = event=>{
        setTouched(current=>({ ...current, [event.target.name]:true }));
    };

    const submit = async event=>{
        event.preventDefault();
        const result = validateProfileForm("personal", values);
        if (!result.isValid) {
            setServerValidation(null);
            setTouched(touchAllFields());
            setSubmitState("validationError");
            return;
        }
        setIsSubmitting(true);
        try {
            const reply = await meBeam.act.updatePersonal(result.values);
            handleProfileSubmitReply(reply, {
                fields:personalFieldNames,
                setIsSubmitting,
                setServerValidation,
                setSubmitState,
                setTouched,
                onDone:onSaved
            })
        } catch(err) {
            setSubmitState("submitError");
            setIsSubmitting(false);
        }
    };

    const renderField = fieldName=>{
        const field = fieldsByName[fieldName];
        const fieldError = getValidationFieldError(validation, field.name);
        const showError = !!fieldError && (showAllErrors || touched[field.name]);
        return (
            <PersonalFormField
                key={field.name}
                field={field}
                fieldError={fieldError}
                onBlur={touchField}
                onChange={setFieldValue}
                showError={showError}
                value={values[field.name]}
            />
        );
    };

    return (
        <Block level={0} className="PersonalForm popScreen" caption={say("profilePersonalPopTitle")}>
            <Box className="BlimpMuiForm" component="form" onSubmit={submit} noValidate>
                <Typography className="BlimpMuiForm-intro">
                    {say("profilePersonalIntro")}
                </Typography>
                {submitState === "validationError" && (
                    <Alert severity="error">{say("formErrorHighlightedFields")}</Alert>
                )}
                {submitState === "submitError" && (
                    <Alert severity="error">{say("profilePersonalErrorSubmit")}</Alert>
                )}
                <Box className="BlimpMuiForm-sections">
                    {personalFormSections.map(section=>(
                        <BlimpMuiFormSection key={section.id} icon={section.icon} title={say(Say.id("profile", "personal", "section", section.id))}>
                            {section.fields.map(renderField)}
                        </BlimpMuiFormSection>
                    ))}
                </Box>
                <BlimpMuiFormActions
                    onCancel={onCancel}
                    submitDisabled={isSubmitting}
                    submitLabel={isSubmitting ? say("commonSaving") : say("commonSave")}
                />
            </Box>
        </Block>
    );
};
